// src/data/commentTemplates.ts
// ----------------------------
// Repository for the `comment_templates` table — the DB-backed Approve/Decline
// comment snippets + Next-Steps blocks (admin-editable, soft-deletable).
// Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

type CommentTemplateInsert = Database["public"]["Tables"]["comment_templates"]["Insert"];
type CommentTemplateUpdate = Database["public"]["Tables"]["comment_templates"]["Update"];

export interface CommentTemplateRow {
  id: string;
  category?: string;
  title?: string;
  text?: string;
  deactivated?: boolean;
  appends_next_steps?: string;
}

// All templates ordered by category, title. includeDeactivated=false filters out
// soft-deleted rows. Returns { data, error } so the caller can distinguish an
// empty table (→ seed) from a fetch error.
export async function listCommentTemplates(
  includeDeactivated: boolean
): Promise<{ data: CommentTemplateRow[] | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  let q = c.from("comment_templates").select("*");
  if (!includeDeactivated) q = q.eq("deactivated", false);
  const { data, error } = await q.order("category").order("title");
  if (error) {
    logger.warn("listCommentTemplates failed:", error.message);
    return { data: null, error };
  }
  return { data: (data ?? []) as unknown as CommentTemplateRow[], error: null };
}

// Insert one or more templates (seed-on-first-load, missing-seed top-up, admin
// create). Takes loose row objects; cast to the generated Insert internally.
export async function insertCommentTemplates(
  rows: object[]
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("comment_templates").insert(rows as CommentTemplateInsert[]);
  if (error) logger.warn("insertCommentTemplates failed:", error.message);
  return { error };
}

// Patch a template by id (admin edit, soft-delete via { deactivated: true },
// reactivate via { deactivated: false }).
export async function updateCommentTemplateById(
  id: string,
  patch: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("comment_templates")
    .update(patch as CommentTemplateUpdate)
    .eq("id", id);
  if (error) logger.warn("updateCommentTemplateById failed:", error.message);
  return { error };
}
